import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// "View as vendor" mode: admin/agent can impersonate a vendor (with their consent)
// by storing the vendor id under this localStorage key. Every API request then
// automatically sets the X-View-As-Vendor header. Set/cleared via the small
// helpers below — components don't read localStorage directly.
const VIEW_AS_KEY = "rentalcar:view_as_vendor";

export function getViewAsVendor() {
  try { return localStorage.getItem(VIEW_AS_KEY) || null; } catch { return null; }
}

export function setViewAsVendor(vendorId, label = null, redirectTo = null) {
  try {
    if (vendorId) {
      localStorage.setItem(VIEW_AS_KEY, vendorId);
      if (label) localStorage.setItem(`${VIEW_AS_KEY}:label`, label);
    } else {
      localStorage.removeItem(VIEW_AS_KEY);
      localStorage.removeItem(`${VIEW_AS_KEY}:label`);
    }
  } catch {
    /* private-mode Safari — degrade gracefully */
  }
  // Refresh the page so React re-fetches every protected resource under the new identity.
  // Iter 67ag — when caller passes `redirectTo` we navigate there instead of
  // reloading the current page, giving "View as vendor" a one-click path
  // straight into the impersonated vendor dashboard.
  if (typeof window !== "undefined") {
    if (redirectTo) {
      window.location.href = redirectTo;
    } else {
      window.location.reload();
    }
  }
}

export function getViewAsLabel() {
  try { return localStorage.getItem(`${VIEW_AS_KEY}:label`) || null; } catch { return null; }
}

// All requests carry the httpOnly auth cookie automatically (same-origin via Kubernetes ingress).
// Bearer-header path was retired Feb 2026 — see PRD "JWT to httpOnly cookies".
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Stamp every outgoing request with the impersonation header when the admin/agent
// has an active "view as" session. The backend's `get_effective_vendor` validates
// against an approved grant, so even a forged header is safe.
api.interceptors.request.use((config) => {
  const v = getViewAsVendor();
  if (v) config.headers["X-View-As-Vendor"] = v;
  return config;
});

// Iter 67en++++++ — Silently downgrade expected 401s ("guest user", "session
// expired") so they don't trip CRA's react-error-overlay in development. The
// overlay otherwise mistakes a caught-but-logged Axios error for an
// unhandled runtime error, and ships an angry red modal to the user — even
// though the caller's try/catch has the situation fully under control.
//
// Strategy:
//   • For 401 on probe endpoints (/auth/me + everything else): mark the
//     error as `__handled401__` and rethrow. Callers' catch blocks still
//     run. A separate guard around `console.error` filters out errors
//     that carry this flag so the dev overlay stays quiet.
//   • All other status codes propagate unchanged.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      error.__handled401__ = true;
    }
    return Promise.reject(error);
  }
);

// Wrap window.onerror + onunhandledrejection at module load so a 401
// flagged above never reaches react-error-overlay. We only filter axios
// errors carrying the `__handled401__` marker — every other thrown error
// still surfaces normally.
if (typeof window !== "undefined" && !window.__rentalcarAuthFilterInstalled) {
  window.__rentalcarAuthFilterInstalled = true;
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (reason && reason.__handled401__) {
      event.preventDefault();  // hide from CRA overlay
    }
  });
}

export default api;

export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(", ");
  return String(detail);
};

export const fileUrl = (path) => (path ? `${API}/files/${path}` : null);
